// biome-ignore-all lint/suspicious/noExplicitAny: Supabase client is typed as any throughout the portal domain layer
import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import { Session } from '../session/session';
import type { EAN } from '../shared/ean';
import { toMasked } from '../shared/masked-ean';

/** One product to refresh: the real code, and the page slug the scraper opens. */
export type ProductFetchRequest = { readonly ean: EAN; readonly page: string | null };

const FETCH_INSERT_CHUNK = 25;

/**
 * Infrastructure adapter for the observation context that owns Supabase client
 * acquisition and the portal_fetch refresh queue.
 */
@Injectable()
export class ObservationAcquisitionService {
  private readonly session = inject(Session);
  private readonly supabase = inject(SupabaseService);

  private client(): any {
    return this.supabase.client;
  }

  /** portal_fetch rows keyed by an empty u_id are unattributable; fail loudly instead. */
  private requireUserId(): string {
    const u_id = this.session.userId();
    if (!u_id) throw new Error('portal_fetch insert requires a session user id');
    return u_id;
  }

  /**
   * Enqueues an `open_product` refresh for every product that carries a page slug and
   * returns the EANs it did not enqueue. The scraper opens a product by its page, so a
   * product without one never reaches the cache and a caller must not wait for it.
   * The queue is keyed by the stored key; it is encoded here and nowhere above.
   */
  async requestProductFetches(
    requests: readonly ProductFetchRequest[],
    signal?: AbortSignal
  ): Promise<readonly EAN[]> {
    const unscrapable = requests.filter((r) => !r.page).map((r) => r.ean);
    const rows = requests
      .filter((r) => r.page)
      .map((r) => ({
        instrType: 'open_product',
        fetchMsg: { ean: toMasked(r.ean), page: r.page },
      }));
    await this.insertFetchRows(rows, signal);
    return unscrapable;
  }

  // Wire shape: u_id_time=`${u_id}_${ts}_${idx}`, u_id, instr_type, fetch_msg, outcome.
  private async insertFetchRows(
    requests: readonly { instrType: string; fetchMsg: unknown }[],
    signal?: AbortSignal
  ): Promise<void> {
    if (requests.length === 0) return;
    const u_id = this.requireUserId();
    const supabase = this.client();
    const ts = Date.now();
    const rows = requests.map(({ instrType, fetchMsg }, idx) => ({
      u_id_time: `${u_id}_${ts}_${idx}`,
      u_id,
      instr_type: instrType,
      fetch_msg: fetchMsg,
      outcome: null,
    }));
    for (let i = 0; i < rows.length; i += FETCH_INSERT_CHUNK) {
      signal?.throwIfAborted();
      const { error } = await supabase
        .from('portal_fetch')
        .insert(rows.slice(i, i + FETCH_INSERT_CHUNK));
      if (error) throw new Error(error.message);
    }
  }
}
